"use client"

import { useTheme } from "next-themes"
import { useEffect, useRef, useState, Fragment } from "react"
import Link from "next/link"
import Image from "next/image"
import { cn } from "../lib/utils"
import { APP_VERSION as SHARED_APP_VERSION } from "@askfinz/shared-code"

export interface FooterLink {
  label: string
  href: string
}

export interface SocialLink {
  label: string
  href: string
  icon: string
}

interface FooterProps {
  /** Callback when version is clicked */
  onVersionClick?: () => void
  /** Footer variant */
  variant?: "default" | "auth"
  /** Current version */
  version?: string
  /** Copyright year override */
  copyrightYear?: number
  /** Company logo URL */
  companyLogo?: string
  /** Company name */
  companyName?: string
  /** Company website URL */
  companyUrl?: string
  /** Footer links */
  links?: FooterLink[]
  /** Social media links */
  /** Social media links */
  socialLinks?: SocialLink[]
  /** Additional class names */
  className?: string
}

const DEFAULT_LINKS: FooterLink[] = [
  { label: "Privacy Policy", href: "https://askfinz.ai/privacy" },
  { label: "Terms of Use", href: "https://askfinz.ai/terms" },
  { label: "Portfolio & Investment Disclaimer", href: "https://askfinz.ai/investment-disclaimer" },
  { label: "Accessibility", href: "https://askfinz.ai/accessibility" },
]

// Only profiles askFinz actually owns, the same set askfinz.com lists as `sameAs` in its Organization
// markup. WhatsApp and Facebook were "#" placeholders with no profile behind them, so they are gone
// rather than kept as dead links.
const DEFAULT_SOCIAL_LINKS: SocialLink[] = [
  { label: "Twitter", href: "https://twitter.com/askfinz", icon: "/shared/social_icons/twitter.png" },
  { label: "LinkedIn", href: "https://www.linkedin.com/company/askfinz", icon: "/shared/social_icons/linkedin.png" },
  { label: "YouTube", href: "https://www.youtube.com/@askfinz", icon: "/shared/social_icons/youtube.png" },
  { label: "Instagram", href: "https://www.instagram.com/askfinz", icon: "/shared/social_icons/instagram.png" },
  { label: "Reddit", href: "https://www.reddit.com/r/askFinz/", icon: "/shared/social_icons/reddit.png" },
  { label: "Crunchbase", href: "https://www.crunchbase.com/organization/askfinz", icon: "/shared/social_icons/crunchbase.png" },
  { label: "Email", href: "mailto:info@askfinz.ai", icon: "/shared/social_icons/email.png" },
]

export function Footer({
  onVersionClick,
  variant = "default",
  version = SHARED_APP_VERSION,
  copyrightYear,
  companyLogo = "/shared/logos/footer/PAUL V Holdings (Dark).svg",
  companyName = "PAUL V Holdings",
  companyUrl = "https://paulvholdings.com",
  links = DEFAULT_LINKS,
  socialLinks = DEFAULT_SOCIAL_LINKS,
  className,
}: FooterProps) {
  const { theme, systemTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  const [currentTheme, setCurrentTheme] = useState<"light" | "dark">("light")
  const [dynamicVersion, setDynamicVersion] = useState(version)

  useEffect(() => {
    setMounted(true)
    
    // Fetch latest version from central API to sync across all webapps
    const releaseApiUrl = "https://askfinz.ai/api/release-notes"
    fetch(releaseApiUrl)
      .then(res => res.json())
      .then(data => {
        if (data.notes && data.notes.length > 0) {
          setDynamicVersion(data.notes[0].version)
        }
      })
      .catch(() => { /* version sync unavailable — use bundled version */ })
  }, [])

  useEffect(() => {
    if (mounted) {
      const resolvedTheme = theme === "system" ? systemTheme : theme
      setCurrentTheme(resolvedTheme === "dark" ? "dark" : "light")
    }
  }, [theme, systemTheme, mounted])

  // Publish the footer's ACTUAL rendered height to `--footer-height` so every
  // consumer (sidebar height-calc, page padding-bottom, app-shell content) keeps
  // its last row above the responsive fixed footer at EVERY breakpoint (the old
  // hardcoded 45px under-reserved on desktop → footer overlapped the sidebar).
  // Declared BEFORE the `!mounted` early return (hooks must be unconditional);
  // deps [mounted] so it measures once the footer element actually exists.
  const footerRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const el = footerRef.current
    if (!el) return
    const apply = () =>
      document.documentElement.style.setProperty("--footer-height", `${el.offsetHeight}px`)
    apply()
    const ro = new ResizeObserver(apply)
    ro.observe(el)
    window.addEventListener("resize", apply)
    return () => { ro.disconnect(); window.removeEventListener("resize", apply) }
  }, [mounted])

  if (!mounted) {
    return null
  }

  const currentYear = copyrightYear || new Date().getFullYear()

  const textSizeClass = variant === "auth"
    ? "text-[7px] sm:text-[8px] md:text-[9px]"
    : "text-[9px] sm:text-[10px] md:text-xs lg:text-sm"

  const logoSizeClass = variant === "auth"
    ? "h-2 w-auto sm:h-2.5 md:h-3 max-w-[40px] sm:max-w-[50px] md:max-w-[60px]"
    : "h-3 w-auto sm:h-3.5 md:h-4 lg:h-5 max-w-[50px] sm:max-w-[60px] md:max-w-[80px]"

  const iconSizeClass = variant === "auth"
    ? "h-2 w-2 sm:h-2.5 sm:w-2.5 md:h-3 md:w-3"
    : "h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-3.5 md:w-3.5"

  const footerClasses = cn(
    variant === "auth"
      ? "app-footer relative flex-shrink-0 bg-background border-t px-4 sm:px-8 z-[100] m-0 py-1"
      : "app-footer fixed bottom-0 left-0 right-0 flex-shrink-0 bg-background border-t px-4 sm:px-16 z-[100] m-0",
    className
  )

  return (
    <footer ref={footerRef} className={footerClasses}>
      <div className={cn(
        "flex flex-nowrap items-center justify-between gap-0.5 sm:gap-1 md:gap-2 lg:gap-4 xl:gap-6 max-w-7xl mx-auto overflow-x-auto hide-scrollbar",
        variant === "auth" ? "py-0.5" : "py-1 sm:py-1.5 md:py-2 lg:py-3"
      )}>
        {/* Left: Copyright and Logo */}
        <div className={cn("flex items-center gap-0.5 sm:gap-1 text-muted-foreground whitespace-nowrap flex-shrink-0", textSizeClass)}>
          <span>© {currentYear} /</span>
          <a
            href={companyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center opacity-70 hover:opacity-100 transition-opacity"
          >
            <img
              src={companyLogo}
              alt={`${companyName} Logo`}
              className={cn("object-contain flex-shrink-0", logoSizeClass)}
            />
          </a>
        </div>

        {/* Center: Links */}
        <div className={cn("flex items-center gap-0.5 sm:gap-1 md:gap-2 text-muted-foreground whitespace-nowrap flex-shrink-0", textSizeClass)}>
          {links.map((link, index) => (
            <Fragment key={link.href}>
              <a
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-foreground transition-colors px-0.5 sm:px-1 md:px-2"
              >
                {link.label}
              </a>
              {index < links.length - 1 && (
                <span className={textSizeClass}>|</span>
              )}
            </Fragment>
          ))}
        </div>

        {/* Right: Version and Social Icons */}
        <div className={cn("flex items-center gap-0.5 sm:gap-1 md:gap-2 text-muted-foreground whitespace-nowrap flex-shrink-0", textSizeClass)}>
          {onVersionClick && (
            <>
              <button
                onClick={onVersionClick}
                className="hover:text-foreground transition-colors cursor-pointer"
                title="View release notes"
              >
                v{dynamicVersion}
              </button>
              <span className={textSizeClass}>|</span>
            </>
          )}
          {socialLinks.map((social) => (
            <a
              key={social.label}
              href={social.href}
              target="_blank"
              rel="noreferrer"
              className="hover:opacity-80 transition-opacity"
              aria-label={social.label}
            >
              <Image
                src={social.icon}
                alt={social.label}
                width={12}
                height={12}
                className={cn("grayscale", iconSizeClass)}
              />
            </a>
          ))}
        </div>
      </div>
    </footer>
  )
}
